import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Zap, Home } from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";



const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    
    if (attempt >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        {  }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        setCreditsAdded(response.data.credits_added || 0);
        // Refresh user data
        checkAuth();
      } else if (response.data.status === "expired") {
        setStatus("failed");
      } else {
        // Continue polling
        setAttempts(attempt + 1);
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        <img src={falcoLogo} alt="Falco" className="h-16 mx-auto mb-6" />

        {status === "checking" && (
          <div className="animate-fade-in" data-testid="payment-checking">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-heading font-bold text-[#2E3A59] mb-2">
              Verifying Payment...
            </h2>
            <p className="text-slate-500">Please wait while we confirm your payment</p>
            <p className="text-xs text-slate-400 mt-2">Attempt {attempts + 1} of 5</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in" data-testid="payment-success">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-[#2E3A59] mb-2">
              Payment Successful!
            </h2>
            <div className="flex items-center justify-center gap-2 text-[#C9A227] mb-4">
              <Zap className="w-6 h-6" />
              <span className="text-2xl font-bold">+{creditsAdded} Credits</span>
            </div>
            <p className="text-slate-500 mb-6">
              Your credits have been added to your account
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full h-12 bg-[#2E3A59] hover:bg-[#1A2236]"
              data-testid="go-to-dashboard-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in" data-testid="payment-failed">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-[#2E3A59] mb-2">
              Payment Failed
            </h2>
            <p className="text-slate-500 mb-6">
              We couldn't verify your payment. Please try again or contact support.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full h-12 bg-[#2E3A59] hover:bg-[#1A2236]"
            >
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
